/* eslint-disable react/no-unescaped-entities */
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { User, Mail, Phone, Building2, Shield, Loader2, Save, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  useDocumentTitle('Profile Settings');
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put(`/users/${user.user_id}`, {
        full_name: profile.full_name,
        phone: profile.phone,
      });
      
      toast.success('Profile updated successfully');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleLabels = {
      'admin': { label: 'Admin', variant: 'destructive' },
      'ptc_admin': { label: 'PTC Admin', variant: 'destructive' },
      'ptc_director': { label: 'PTC Director', variant: 'default' },
      'ptc_manager': { label: 'PTC Manager', variant: 'default' },
      'ptc_rep': { label: 'PTC Rep', variant: 'secondary' },
      'company_admin': { label: 'Company Admin', variant: 'destructive' },
      'company_director': { label: 'Director', variant: 'default' },
      'company_manager': { label: 'Manager', variant: 'default' },
      'company_team_member': { label: 'Team Member', variant: 'secondary' },
    };
    
    const config = roleLabels[role] || { label: role, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary">Profile Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account information</p>
      </div>

      {/* Account Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-accent" />
            Account Overview
          </CardTitle>
          <CardDescription>Your account details and permissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-lg font-bold text-accent">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="font-medium text-slate-900">{user?.full_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>
            {getRoleBadge(user?.role)}
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Company</p>
              <p className="font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                {user?.company_name || 'Not assigned'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Account Type</p>
              <p className="font-medium flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-400" />
                {user?.company_type === 'internal' ? 'Internal User' : 'Partner User'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile */}
      <Card>
        <CardHeader>
          <CardTitle>Edit Profile</CardTitle>
          <CardDescription>Update your personal information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={profile.full_name}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  placeholder="Enter your full name"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    disabled
                    className="pl-10 bg-slate-50"
                    data-testid="profile-email-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Email cannot be changed. Contact support if needed.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="pl-10"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" disabled={saving} className="bg-accent hover:bg-gold-dark">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Password Management</p>
              <p className="text-sm text-blue-700">
                To change your password, use the "Change Password" option from the dropdown menu in the sidebar.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
